import "./InvoiceTable.css";
import React, { useEffect, useState } from "react";
import type { Column } from "@frontend/Models/Table/TableModels";
import InvoiceService from "../../../../AppSettings/services/InvoiceService";
import InvoiceWizard from "../InvoiceWizard/InvoiceWizard";
import type { UninvoicedInvoiceScope } from '../../../../AppSettings/services/InvoiceService';
import VoidInvoiceModal from "../VoidInvoiceModal/VoidInvoiceModal";
import Table from "../../../../Components/Table/Table";

type Client = {
  name?: string;
  email?: string;
};

export type Invoice = {
  invoiceid?: string;
  invoice_number?: number | null;
  created_at?: string | null;
  due_date?: string | null;
  billing_period_start?: string | null;
  billing_period_end?: string | null;
  total_amount?: number | string | null;
  amount_paid?: number | string | null;
  acc_rec_total?: number | string | null;
  acc_pay_total?: number | string | null;
  sent_amount?: number | string | null;
  saved_amount?: number | string | null;
  overdue_amount?: number | string | null;
  paid?: boolean;
  client?: Client;
  status?: string;
  voided_at?: string | null;
};

type ViewMode = "table" | "card";

export interface InvoiceTableProps {
  showCreateInvoice?: boolean;
  invoices?: Invoice[];
  onInvoiceCreated?: () => void;
  loading?: boolean;
  viewMode?: ViewMode;
  initialClientId?: string;
  initialMatterId?: string;
  initialScope?: UninvoicedInvoiceScope;
}

const InvoiceTable: React.FC<InvoiceTableProps> = ({
  showCreateInvoice = false,
  invoices = [],
  onInvoiceCreated,
  loading = false,
  viewMode = "table",
  initialClientId,
  initialMatterId,
  initialScope,
}) => {
  const [isGenerateInvoiceOpen, setIsGenerateInvoiceOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [editingAmounts, setEditingAmounts] = useState<Record<string, string>>(
    {}
  );
  const [localInvoices, setLocalInvoices] = useState<Invoice[]>(invoices);
  const [voidingInvoiceId, setVoidingInvoiceId] = useState<string | null>(null);

  useEffect(() => {
    if (showCreateInvoice) {
      setIsGenerateInvoiceOpen(true);
      setSelectedInvoice(null);
    }
  }, [showCreateInvoice]);

  useEffect(() => {
    setLocalInvoices(invoices);
  }, [invoices]);

  const toggleGenerateInvoice = () => {
    if (!isGenerateInvoiceOpen) {
      setSelectedInvoice(null);
    }
    setIsGenerateInvoiceOpen((prev) => !prev);
  };

  const handleInvoiceCreated = () => {
    setIsGenerateInvoiceOpen(false);
    setSelectedInvoice(null);
    if (onInvoiceCreated) {
      onInvoiceCreated();
    }
  };

  const handleVoidComplete = () => {
    setVoidingInvoiceId(null);
    if (onInvoiceCreated) {
      onInvoiceCreated();
    }
  };

  const handleInvoiceClick = async (invoice: Invoice) => {
    if (!invoice.invoiceid) return;
    try {
      const invoiceDetails = await InvoiceService.getInvoiceById(
        invoice.invoiceid
      );
      setSelectedInvoice(invoiceDetails);
      setIsGenerateInvoiceOpen(true);
    } catch (error) {
      console.error("Failed to fetch invoice details:", error);
    }
  };

  const handleAmountPaidChange = (
    invoiceId: string | undefined,
    value: string
  ) => {
    if (!invoiceId) return;
    setEditingAmounts((prev) => ({
      ...prev,
      [invoiceId]: value,
    }));
  };

  const handleAmountPaidBlur = async (
    invoiceId: string | undefined,
    value: string
  ) => {
    if (!invoiceId) return;
    try {
      const amountPaid = parseFloat(value) || 0;
      const totalAmount = parseFloat(
        (localInvoices.find((inv) => inv.invoiceid === invoiceId)?.total_amount ??
          0) as string
      );
      const isPaid = amountPaid >= totalAmount;

      setLocalInvoices((prevInvoices) =>
        prevInvoices.map((invoice) =>
          invoice.invoiceid === invoiceId
            ? { ...invoice, amount_paid: amountPaid.toString(), paid: isPaid }
            : invoice
        )
      );

      await InvoiceService.updateInvoice({
        invoiceid: invoiceId,
        amount_paid: amountPaid.toString(),
        paid: isPaid,
      });

      setEditingAmounts((prev) => {
        const next = { ...prev };
        delete next[invoiceId];
        return next;
      });
    } catch (error) {
      console.error("Failed to update amount paid:", error);
      setLocalInvoices(invoices);
    }
  };

  const handlePaidCheckboxChange = async (
    invoiceId: string | undefined,
    isPaid: boolean
  ) => {
    if (!invoiceId) return;
    try {
      const invoice = localInvoices.find((inv) => inv.invoiceid === invoiceId);
      const amountPaid = isPaid ? invoice?.total_amount ?? "0" : "0";

      setLocalInvoices((prevInvoices) =>
        prevInvoices.map((inv) =>
          inv.invoiceid === invoiceId
            ? { ...inv, amount_paid: amountPaid, paid: isPaid }
            : inv
        )
      );

      await InvoiceService.updateInvoice({
        invoiceid: invoiceId,
        amount_paid: amountPaid,
        paid: isPaid,
      });
    } catch (error) {
      console.error("Failed to update paid status:", error);
      setLocalInvoices(invoices);
    }
  };

  const getInvoiceLinkClassName = (invoice: Invoice) => {
    if (invoice.status === "void") return "invoice-link--void";
    const amountPaid = parseFloat((invoice.amount_paid ?? 0) as string);
    const totalAmount = parseFloat((invoice.total_amount ?? 0) as string);

    if (amountPaid >= totalAmount) {
      return "invoice-link--paid";
    }
    if (amountPaid > 0) {
      return "invoice-link--partial";
    }
    return "invoice-link--unpaid";
  };

  const deriveStatus = (invoice: Invoice) => {
    if (invoice.status === "void") return "Void";
    const amountPaid = parseFloat((invoice.amount_paid ?? 0) as string);
    const totalAmount = parseFloat((invoice.total_amount ?? 0) as string);
    if (amountPaid >= totalAmount) return "Paid";
    if (amountPaid > 0) return "Partial";
    return "Un-Paid";
  };

  const statusClassMap: Record<string, string> = {
    Paid: "paid",
    Partial: "partial",
    "Un-Paid": "unpaid",
    Void: "void",
  };
  const statusClass = (status: string) => statusClassMap[status] ?? "unpaid";

  const formatCurrency = (value?: number | string | null) => {
    const amount = parseFloat(String(value ?? 0));
    return amount.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  };

  const formatDate = (value?: string | null) => {
    if (!value) return "—";
    // Date-only (YYYY-MM-DD) must not be parsed as UTC midnight — that shifts
    // the calendar day westward of UTC.
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (dateOnly) {
      const [, y, m, d] = dateOnly;
      const local = new Date(Number(y), Number(m) - 1, Number(d));
      return local.toLocaleDateString();
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
  };

  const formatBillingPeriod = (start?: string | null, end?: string | null) => {
    if (!start && !end) return "—";
    if (start && end) return `${formatDate(start)} – ${formatDate(end)}`;
    return formatDate(start || end);
  };

  const daysUntil = (value?: string | null) => {
    if (!value) return null;
    const target = new Date(value);
    if (Number.isNaN(target.getTime())) return null;
    const diff = target.getTime() - Date.now();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return days;
  };

  const columns: Column<Invoice>[] = [
    {
      header: "Invoice",
      render: (invoice) => (
        <div className="invoice-cell">
          <button
            type="button"
            onClick={() => handleInvoiceClick(invoice)}
            className={`invoice-link ${getInvoiceLinkClassName(invoice)}`}
          >
            {invoice.invoice_number ?? invoice.invoiceid ?? "—"}
          </button>
          {invoice.invoiceid && (
            <span className="invoice-cell-meta">ID: {invoice.invoiceid}</span>
          )}
          <span className="invoice-cell-meta">
            Created {formatDate(invoice.created_at)}
          </span>
        </div>
      ),
    },
    {
      header: "Client",
      render: (invoice) => (
        <div className="invoice-client-cell">
          <div className="avatar-fallback">
            {(invoice.client?.name || "C").slice(0, 1)}
          </div>
          <div className="client-name">{invoice.client?.name || "Client"}</div>
        </div>
      ),
    },
    {
      header: "Status",
      render: (invoice) => {
        const status = deriveStatus(invoice);
        return (
          <span className={`status-chip ${statusClass(status)}`}>
            {status}
          </span>
        );
      },
    },
    {
      header: "Billing Period",
      render: (invoice) => (
        <div className="invoice-date-cell">
          {formatBillingPeriod(invoice.billing_period_start, invoice.billing_period_end)}
        </div>
      ),
    },
    {
      header: "Due",
      render: (invoice) => {
        const days = daysUntil(invoice.due_date);
        return (
          <div className="invoice-date-cell">
            <div>{formatDate(invoice.due_date)}</div>
            <div className="sub-text">
              {days === null
                ? "—"
                : `${Math.max(days, 0)} days ${days >= 0 ? "left" : "overdue"}`}
            </div>
          </div>
        );
      },
    },
    {
      header: "Total",
      render: (invoice) => (
        <div className="amount-cell">
          {formatCurrency(invoice.total_amount)}
        </div>
      ),
    },
    {
      header: "Amount Paid",
      render: (invoice) => (
        <div className="invoice-amount-paid-cell">
          <input
            type="number"
            step="0.01"
            min="0"
            className="invoice-amount-paid-input"
            value={
              editingAmounts[invoice.invoiceid ?? ""] !== undefined
                ? editingAmounts[invoice.invoiceid ?? ""]
                : (invoice.amount_paid as string) || ""
            }
            onChange={(e) =>
              handleAmountPaidChange(invoice.invoiceid, e.target.value)
            }
            onBlur={(e) =>
              handleAmountPaidBlur(invoice.invoiceid, e.target.value)
            }
            placeholder="0.00"
            disabled={invoice.status === "void"}
          />
        </div>
      ),
    },
    {
      header: "Mark Paid",
      render: (invoice) => (
        <div className="invoice-checkbox-cell">
          <input
            type="checkbox"
            checked={
              invoice.paid ||
              (parseFloat((invoice.amount_paid ?? 0) as string) >=
                parseFloat((invoice.total_amount ?? 0) as string) &&
                parseFloat((invoice.total_amount ?? 0) as string) > 0)
            }
            onChange={(e) =>
              handlePaidCheckboxChange(invoice.invoiceid, e.target.checked)
            }
            className="invoice-paid-checkbox"
            disabled={invoice.status === "void"}
          />
        </div>
      ),
    },
    {
      header: "Actions",
      render: (invoice) =>
        invoice.status !== "void" ? (
          <div className="invoice-actions-cell">
            <button
              className="void-invoice-btn"
              onClick={(e) => {
                e.stopPropagation();
                setVoidingInvoiceId(invoice.invoiceid ?? null);
              }}
            >
              Void
            </button>
          </div>
        ) : null,
    },
  ];

  const renderCardView = () => {
    if (localInvoices.length === 0) {
      return (
        <div className="invoice-empty-state">
          <button onClick={toggleGenerateInvoice} className="text-link">
            <h3 style={{ textAlign: "center" }}>Click to add new invoice</h3>
          </button>
        </div>
      );
    }

    return (
      <div className="invoice-card-grid">
        {localInvoices.map((invoice) => {
          const status = deriveStatus(invoice);
          const days = daysUntil(invoice.due_date);
          
          return (
            <div 
              key={invoice.invoiceid} 
              className={`invoice-card ${statusClass(status)}`}
              onClick={() => handleInvoiceClick(invoice)}
            >
              <div className="invoice-card-header">
                <div className="invoice-card-id">
                  <button
                    className={`invoice-link ${getInvoiceLinkClassName(invoice)}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleInvoiceClick(invoice);
                    }}
                  >
                    {invoice.invoice_number ?? invoice.invoiceid ?? "—"}
                  </button>
                  {invoice.invoiceid && (
                    <div className="sub-text">ID: {invoice.invoiceid}</div>
                  )}
                </div>
                <div className="invoice-card-header-actions">
                  <span className={`status-chip ${statusClass(status)}`}>
                    {status}
                  </span>
                  {invoice.status !== "void" && (
                    <button
                      className="void-invoice-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setVoidingInvoiceId(invoice.invoiceid ?? null);
                      }}
                    >
                      Void
                    </button>
                  )}
                </div>
              </div>
              
              <div className="invoice-card-client">
                <div className="avatar-fallback">
                  {(invoice.client?.name || "C").slice(0, 1)}
                </div>
                <div className="invoice-card-client-info">
                  <div className="client-name">{invoice.client?.name || "Client"}</div>
                </div>
              </div>

              <div className="invoice-card-details">
                <div className="invoice-card-row">
                  <span className="invoice-card-label">Created:</span>
                  <span>{formatDate(invoice.created_at)}</span>
                </div>
                <div className="invoice-card-row">
                  <span className="invoice-card-label">Billing Period:</span>
                  <span>
                    {formatBillingPeriod(
                      invoice.billing_period_start,
                      invoice.billing_period_end
                    )}
                  </span>
                </div>
                <div className="invoice-card-row">
                  <span className="invoice-card-label">Due:</span>
                  <span>
                    {formatDate(invoice.due_date)}
                    {days !== null && (
                      <span className="due-days">
                        {" "}({Math.max(days, 0)} days {days >= 0 ? "left" : "overdue"})
                      </span>
                    )}
                  </span>
                </div>
                <div className="invoice-card-row">
                  <span className="invoice-card-label">Total:</span>
                  <span className="amount-cell">
                    {formatCurrency(invoice.total_amount)}
                  </span>
                </div>
                <div className="invoice-card-row">
                  <span className="invoice-card-label">Amount Paid:</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={
                      editingAmounts[invoice.invoiceid ?? ""] !== undefined
                        ? editingAmounts[invoice.invoiceid ?? ""]
                        : (invoice.amount_paid as string) || ""
                    }
                    onChange={(e) => {
                      e.stopPropagation();
                      handleAmountPaidChange(invoice.invoiceid, e.target.value);
                    }}
                    onBlur={(e) => {
                      e.stopPropagation();
                      handleAmountPaidBlur(invoice.invoiceid, e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    placeholder="0.00"
                    className="invoice-card-amount-input"
                    disabled={invoice.status === "void"}
                  />
                </div>
                <div className="invoice-card-row">
                  <span className="invoice-card-label">Mark Paid:</span>
                  <input
                    type="checkbox"
                    checked={
                      invoice.paid ||
                      (parseFloat((invoice.amount_paid ?? 0) as string) >=
                        parseFloat((invoice.total_amount ?? 0) as string) &&
                        parseFloat((invoice.total_amount ?? 0) as string) > 0)
                    }
                    onChange={(e) => {
                      e.stopPropagation();
                      handlePaidCheckboxChange(invoice.invoiceid, e.target.checked);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="paid-checkbox"
                    disabled={invoice.status === "void"}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return <div className="loading">Loading invoices...</div>;
  }

  return (
    <div className="invoice-page-shell">
      {viewMode === "table" ? (
        <div className="invoice-table-wrapper invoice-data-table">
          <Table
            data={localInvoices}
            rowKey="invoiceid"
            columns={columns}
            emptyMessage="No invoices found"
            emptyAction={
              <button onClick={toggleGenerateInvoice} className="text-link">
                <h3 style={{ textAlign: "center" }}>Click to add new invoice</h3>
              </button>
            }
          />
        </div>
      ) : (
        renderCardView()
      )}

      {isGenerateInvoiceOpen && (
        <InvoiceWizard
          onClose={handleInvoiceCreated}
          existingInvoice={selectedInvoice}
          initialClientId={!selectedInvoice ? initialClientId : undefined}
          initialMatterId={!selectedInvoice ? initialMatterId : undefined}
          initialScope={!selectedInvoice ? initialScope : undefined}
          readOnly={selectedInvoice?.status === "void"}
        />
      )}

      {voidingInvoiceId && (
        <VoidInvoiceModal
          invoiceId={voidingInvoiceId}
          displayNumber={
            invoices?.find((i) => i.invoiceid === voidingInvoiceId)?.invoice_number ?? voidingInvoiceId
          }
          onClose={() => setVoidingInvoiceId(null)}
          onVoided={handleVoidComplete}
        />
      )}
    </div>
  );
};

export default InvoiceTable;
